import React from 'react';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import CircularProgress from '@material-ui/core/CircularProgress';
import InputAdornment from '@material-ui/core/InputAdornment';
import SearchIcon from '@material-ui/icons/Search';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  root: {
    flex: 1,
    minWidth: 0,
    width: '100%',
    maxWidth: 420,
  },
  autocomplete: {
    width: '100%',
    '& .MuiAutocomplete-inputRoot': {
      paddingTop: '0 !important',
      paddingBottom: '0 !important',
      paddingLeft: '8px !important',
      minHeight: 40,
      backgroundColor: '#f5f8fc',
      borderRadius: 8,
      transition: 'background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease',
      '& fieldset': {
        borderColor: '#e7ebf0',
        borderWidth: '1px !important',
      },
      '&:hover': {
        backgroundColor: '#fff',
        '& fieldset': {
          borderColor: '#d5dde8',
        },
      },
      '&.Mui-focused': {
        backgroundColor: '#fff',
        boxShadow: '0 0 0 3px rgba(61, 125, 221, 0.12)',
        '& fieldset': {
          borderColor: `${theme.palette.primary.main} !important`,
          borderWidth: '1px !important',
        },
      },
    },
    '& .MuiAutocomplete-input': {
      padding: '10px 8px 10px 0 !important',
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#263548',
      '&::placeholder': {
        color: '#647184',
        opacity: 1,
      },
    },
    '& .MuiAutocomplete-endAdornment': {
      right: 8,
    },
    '& .MuiAutocomplete-clearIndicator, & .MuiAutocomplete-popupIndicator': {
      color: '#647184',
      padding: 4,
      '&:hover': {
        backgroundColor: 'rgba(61, 125, 221, 0.08)',
        color: theme.palette.primary.main,
      },
    },
  },
  searchIcon: {
    color: '#94a3b8',
    fontSize: '1.125rem',
    marginLeft: 4,
  },
  paper: {
    marginTop: 6,
    borderRadius: 8,
    border: '1px solid #e7ebf0',
    boxShadow: '0 8px 24px rgba(12, 41, 84, 0.1)',
    overflow: 'hidden',
  },
  listbox: {
    padding: theme.spacing(0.75),
    maxHeight: 320,
    '& .MuiAutocomplete-option': {
      minHeight: 40,
      borderRadius: 6,
      margin: theme.spacing(0.25, 0),
      padding: theme.spacing(1, 1.25),
      fontSize: '0.8125rem',
      fontWeight: 500,
      color: '#263548',
      lineHeight: 1.4,
      '&[data-focus="true"]': {
        backgroundColor: 'rgba(61, 125, 221, 0.08)',
      },
      '&[aria-selected="true"]': {
        backgroundColor: 'rgba(61, 125, 221, 0.12)',
      },
    },
  },
  noOptions: {
    fontSize: '0.8125rem',
    color: '#647184',
    padding: theme.spacing(1.5, 1.75),
  },
  loadingText: {
    fontSize: '0.8125rem',
    color: '#647184',
    padding: theme.spacing(1.5, 1.75),
  },
  optionPrimary: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  optionSecondary: {
    fontSize: '0.6875rem',
    fontWeight: 500,
    color: '#647184',
    marginTop: 2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

export default function DynamicAutoSearch({
  open,
  options,
  handleOpen,
  handleSearch,
  handleChange,
  optLoading = '',
  placeholder = 'Search courses…',
}) {
  const classes = useStyles();
  const loading = optLoading === '' ? open && options.length === 0 : optLoading === true || optLoading === 'loading';

  return (
    <div className={classes.root}>
      <Autocomplete
        id="header-course-search"
        className={classes.autocomplete}
        open={open}
        onOpen={() => {
          handleOpen(true);
        }}
        onClose={() => {
          handleOpen(false);
        }}
        onChange={handleChange}
        getOptionSelected={(option, value) => option.name === value.name}
        getOptionLabel={(option) => option.name || ''}
        options={options}
        loading={loading}
        classes={{
          paper: classes.paper,
          listbox: classes.listbox,
          noOptions: classes.noOptions,
          loading: classes.loadingText,
        }}
        noOptionsText="No courses found"
        loadingText="Searching…"
        renderOption={(option) => (
          <div style={{ minWidth: 0, width: '100%' }}>
            <div className={classes.optionPrimary} title={option.name}>
              {option.name}
            </div>
            {option.category ? (
              <div className={classes.optionSecondary}>{option.category}</div>
            ) : null}
          </div>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            fullWidth
            placeholder={placeholder}
            onChange={(ev) => {
              handleSearch(ev.target.value);
            }}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon className={classes.searchIcon} />
                </InputAdornment>
              ),
              endAdornment: (
                <React.Fragment>
                  {loading ? <CircularProgress color="inherit" size={16} style={{ color: '#94a3b8', marginRight: 4 }} /> : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
          />
        )}
      />
    </div>
  );
}
